/**
 * Demo/Sales page for potential BUYERS of the software
 * Features YouTube video and key selling points for acquisition
 */
export default function DemoPage() {
  return (
    <div
      className="demo-page"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f0a1f 0%, #1a1333 50%, #0f0a1f 100%)',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <style>{`
        * { box-sizing: border-box; }
        .demo-page::-webkit-scrollbar { width: 0; display: none; }
        .demo-page { -ms-overflow-style: none; scrollbar-width: none; }

        .feature-card:hover {
          transform: translateY(-4px);
          border-color: rgba(139, 92, 246, 0.5);
        }

        .cta-button:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.4);
        }

        .check-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .check-icon {
          color: #22C55E;
          font-size: 20px;
          flex-shrink: 0;
          margin-top: 2px;
        }
      `}</style>

      {/* Content */}
      <div
        style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '40px 24px 80px',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '48px',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              borderRadius: '24px',
              background: 'rgba(234, 179, 8, 0.15)',
              border: '1px solid rgba(234, 179, 8, 0.3)',
            }}
          >
            <span style={{ fontSize: '14px', color: '#EAB308', fontWeight: 600 }}>
              FOR SALE: Complete AI Tutoring Platform
            </span>
          </div>
        </header>

        {/* Main Hero */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '48px',
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-1px',
              margin: '0 0 24px 0',
            }}
          >
            I Built Infrastructure for Scaling AI Tutoring.
            <br />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Accidentally.</span>
          </h1>

          <p
            style={{
              fontSize: 'clamp(17px, 2.5vw, 20px)',
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '700px',
              margin: '0 auto 16px',
              lineHeight: 1.7,
            }}
          >
            Built this for my wife's English tutoring business. 10 paying students use it daily.
            Then I realized: this is bigger than one tutor with ten students.
          </p>

          <p
            style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.5)',
              maxWidth: '600px',
              margin: '0 auto 32px',
              fontStyle: 'italic',
            }}
          >
            I just ask that my wife can continue using the app for her existing private and group tutoring students.
          </p>
        </div>

        {/* Video Section */}
        <div
          style={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: '64px',
          }}
        >
          <div
            style={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
            }}
          >
            <iframe
              src="https://www.youtube.com/embed/3pmuma12p9E?rel=0&modestbranding=1"
              title="AI English Tutor Demo"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        {/* The Story */}
        <div
          style={{
            marginBottom: '64px',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '24px',
              color: '#ffffff',
            }}
          >
            The Story
          </h2>

          <div
            style={{
              fontSize: '17px',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.8,
            }}
          >
            <p style={{ marginBottom: '20px' }}>
              My wife teaches English in Ukraine. Group classes. Private lessons. Her students needed speaking practice between sessions—but they were too afraid to practice with humans.
            </p>
            <p style={{ marginBottom: '20px' }}>
              So I built an AI tutor they could talk to. No judgment. No embarrassment. Just practice.
            </p>
            <p style={{ marginBottom: '20px' }}>
              Then I added mistake tracking. Then auto-generated weekly review lessons from those mistakes. Then a teacher dashboard so she could see everything. Then gamification—streaks, badges, stars—because I noticed her students actually came back when there was something to lose.
            </p>
            <p style={{ marginBottom: '20px', fontWeight: 600, color: '#ffffff' }}>
              I'm a developer. Not a language school operator.
            </p>
            <p style={{ marginBottom: '0' }}>
              This app could serve hundreds of teachers. Thousands of students. Someone with the right distribution—connections to language schools, online tutoring platforms, education marketplaces—could turn this into something much larger.
            </p>
          </div>
        </div>

        {/* What Makes This Sellable */}
        <div
          style={{
            marginBottom: '64px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '20px',
            padding: '40px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '32px',
              color: '#ffffff',
            }}
          >
            Why Schools Will Pay For This
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '32px',
            }}
          >
            {/* Auto Review Lessons */}
            <div>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎯</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#22C55E' }}>
                Auto-Generated Weekly Reviews
              </h3>
              <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>
                AI catches every mistake. Categorizes it. Every Monday, students get a personalized review lesson built from their own screw-ups. Zero teacher prep.
              </p>
            </div>

            {/* Student Intent Tracking */}
            <div>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🧠</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#3B82F6' }}>
                See What Students Want to Learn
              </h3>
              <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>
                When students create topics or practice specific words, teachers see it. The only app that reveals what students actually want—not just what they did.
              </p>
            </div>

            {/* Voice AI */}
            <div>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎤</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#8B5CF6' }}>
                Native Audio—Not Speech-to-Text
              </h3>
              <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>
                Gemini Live processes native audio directly. It hears exactly which sounds students mispronounce—not just transcribed words. No speech-to-text → text-to-speech pipeline. Real pronunciation feedback.
              </p>
            </div>

            {/* Gamification */}
            <div>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏆</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#EAB308' }}>
                Gamification That Actually Works
              </h3>
              <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>
                27 badges. Streaks. Stars. Weekly challenges. Students practice because they can't break their streak—not because you told them to.
              </p>
            </div>
          </div>
        </div>

        {/* The Hard Parts Are Done */}
        <div
          style={{
            marginBottom: '64px',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '32px',
              color: '#ffffff',
            }}
          >
            The Hard Parts Are Done
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '16px',
            }}
          >
            {[
              'Voice AI integration with Gemini Live API',
              'Real-time mistake tracking via function calling',
              'Auto-generated weekly review lessons',
              'Teacher dashboard with class analytics',
              'Student progress tracking & insights',
              '27-badge gamification system',
              'Streak tracking with milestone celebrations',
              'Subscription tiers (Starter/Plus/Unlimited)',
              'Usage limits and billing management',
              'Class codes for group enrollment',
              'Private student invite codes',
              'CEFR levels A1-C2 with adaptive difficulty',
              'PWA that installs on any phone',
              'Multi-language support (English/Ukrainian)',
              '100+ React components, production quality',
            ].map((item, i) => (
              <div key={i} className="check-item">
                <span className="check-icon">✓</span>
                <span style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)' }}>{item}</span>
              </div>
            ))}
          </div>

          <p
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#ffffff',
              marginTop: '32px',
              textAlign: 'center',
            }}
          >
            What's left? Distribution.
          </p>
        </div>

        {/* What You're Getting */}
        <div
          style={{
            marginBottom: '64px',
            background: 'rgba(34, 197, 94, 0.08)',
            borderRadius: '20px',
            padding: '40px',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '32px',
              color: '#22C55E',
            }}
          >
            What You're Getting
          </h2>

          <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.8 }}>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Proven with 10 paying students</strong> — real daily usage, real engagement, proof it works</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Complete source code</strong> — React 18 + TypeScript frontend, FastAPI backend</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Firebase infrastructure</strong> — Auth, Firestore, Storage, ready to transfer</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Teacher dashboard</strong> — create lessons, manage students, view analytics</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Student app</strong> — voice chat, progress tracking, badges, streaks</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Subscription system</strong> — tiers, usage limits, billing management built in</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Gamification engine</strong> — 27 badges, 5 categories, streak celebrations</span>
            </div>
            <div className="check-item">
              <span className="check-icon">✓</span>
              <span><strong>Documentation</strong> — architecture notes, setup guides, environment configs</span>
            </div>
          </div>
        </div>

        {/* Tech Stack */}
        <div
          style={{
            marginBottom: '64px',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '24px',
              color: '#ffffff',
            }}
          >
            Tech Stack
          </h2>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            {[
              'React 18',
              'TypeScript',
              'Vite',
              'Tailwind CSS',
              'Firebase Auth',
              'Firestore',
              'Firebase Storage',
              'FastAPI',
              'Python',
              'Google Gemini Live API',
              'WebSocket',
              'PWA',
            ].map((tech, i) => (
              <span
                key={i}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                {tech}
              </span>
            ))}
          </div>

          <p
            style={{
              marginTop: '24px',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.6,
            }}
          >
            Clean architecture. Production-quality code. 100+ components, 21 custom hooks.
            This isn't a weekend hack—it's months of development work, shipped and working.
          </p>
        </div>

        {/* Revenue Opportunities */}
        <div
          style={{
            marginBottom: '64px',
            background: 'rgba(139, 92, 246, 0.08)',
            borderRadius: '20px',
            padding: '40px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '24px',
              color: '#8B5CF6',
            }}
          >
            Revenue Paths
          </h2>

          <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', lineHeight: 2 }}>
            <p style={{ margin: '0 0 8px 0' }}><strong>B2B to language schools</strong> — teacher dashboard is built for this</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>White-label for tutoring companies</strong> — multi-tenant architecture ready</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>Direct-to-student subscriptions</strong> — tiers already built</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>Corporate English training</strong> — companies pay premium for employee development</p>
            <p style={{ margin: '0 0 8px 0' }}><strong>Expand to other languages</strong> — Spanish, French, German learners</p>
            <p style={{ margin: 0 }}><strong>Affiliate/referral program</strong> — gamification hooks ready for viral mechanics</p>
          </div>
        </div>

        {/* The Numbers */}
        <div
          style={{
            marginBottom: '64px',
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '40px',
              color: '#ffffff',
            }}
          >
            By The Numbers
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '24px',
            }}
          >
            <div>
              <div style={{ fontSize: '48px', fontWeight: 700, color: '#22C55E' }}>10</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Paying Students</div>
            </div>
            <div>
              <div style={{ fontSize: '48px', fontWeight: 700, color: '#3B82F6' }}>100+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Components</div>
            </div>
            <div>
              <div style={{ fontSize: '48px', fontWeight: 700, color: '#8B5CF6' }}>27</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Badges</div>
            </div>
            <div>
              <div style={{ fontSize: '48px', fontWeight: 700, color: '#EAB308' }}>6</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>AI Personas</div>
            </div>
            <div>
              <div style={{ fontSize: '48px', fontWeight: 700, color: '#EC4899' }}>6</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>CEFR Levels</div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div
          style={{
            textAlign: 'center',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '24px',
            padding: '48px 32px',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <h2
            style={{
              fontSize: '32px',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            Working Software. Real Users. Ready to Scale.
          </h2>
          <p
            style={{
              fontSize: '18px',
              color: 'rgba(255, 255, 255, 0.6)',
              marginBottom: '16px',
              maxWidth: '500px',
              margin: '0 auto 16px',
            }}
          >
            The hard parts are done. It needs someone with distribution to take it further.
          </p>
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.4)',
              marginBottom: '32px',
              maxWidth: '500px',
              margin: '0 auto 32px',
              fontStyle: 'italic',
            }}
          >
            I just ask that my wife can continue using the app for her existing private and group tutoring students.
          </p>
          <a
            href="mailto:michaelsuccess35@gmail.com?subject=AI%20English%20Tutor%20Inquiry"
            className="cta-button"
            style={{
              display: 'inline-block',
              padding: '18px 48px',
              borderRadius: '14px',
              border: 'none',
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 200ms ease',
              boxShadow: '0 4px 24px rgba(34, 197, 94, 0.3)',
              textDecoration: 'none',
            }}
          >
            Inquire About This Project
          </a>
        </div>

        {/* Footer */}
        <footer
          style={{
            textAlign: 'center',
            marginTop: '64px',
            paddingTop: '32px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.4)',
              margin: 0,
            }}
          >
            Built with React, TypeScript, Firebase & Google Gemini
          </p>
        </footer>
      </div>
    </div>
  );
}
